import ExcelJS from "exceljs";
import defaultLogoSrc from "assets/TMS-NepalCan-Logo-Quotation.png";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toOrdinalDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getFullYear();
  const suffixes = ["th", "st", "nd", "rd"];
  const v = day % 100;
  const suffix = suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
  return `${day}${suffix} ${month} ${year}`;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const urlToBase64 = (url) =>
  fetch(url)
    .then((r) => r.blob())
    .then(
      (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result.split(",")[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        }),
    );

// ─── Style helpers ─────────────────────────────────────────────────────────────

const C = {
  red: "FFC0392B",
  white: "FFFFFFFF",
  black: "FF000000",
  blue: "FF0000FF",
  yellow: "FFFFFF00",
  lightGray: "FFF5F5F5",
  gridBorder: "FFCCCCCC",
};

const solidFill = (argb) => ({
  type: "pattern",
  pattern: "solid",
  fgColor: { argb },
});
const thinGray = { style: "thin", color: { argb: C.gridBorder } };
const allThinGray = {
  top: thinGray,
  bottom: thinGray,
  left: thinGray,
  right: thinGray,
};

const styleCell = (cell, { font, fill, alignment, border } = {}) => {
  if (font) cell.font = font;
  if (fill) cell.fill = fill;
  if (alignment) cell.alignment = alignment;
  if (border) cell.border = border;
};

// ─── Main Generator ────────────────────────────────────────────────────────────

export const generateVendorQuotationExcel = async (formData) => {
  const {
    brands = [],
    locations = [],
    entries = [],
    title = "Vendor Quotation Excel",
    logoFile = null,
    headerCompanyName = "",
    headerFormerName = "",
    headerRegnNo = "",
    headerPanNo = "",
    date = "",
    toName = "",
    toAddress = "",
    subject = "",
    chargesNote = "",
    termsAndConditions = [],
    closingNote = "",
    thankYouNote = "",
    regards = "",
    signatoryName = "",
    signatoryDesignation = "",
    signatoryCompany = "",
    signatoryAddress = "",
    signatoryPhone = "",
    signatoryEmail = "",
    footerLeft = "",
    footerCenter = "",
    footerRightWeb = "",
    footerRightEmail = "",
  } = formData;

  const flatProducts = brands.flatMap((b) =>
    b.products.map((p) => ({ brandName: b.name, productName: p.name })),
  );
  const TOTAL_COLS = Math.max(1 + flatProducts.length, 5);

  // ── Logo ──
  let logoBase64 = null;
  try {
    logoBase64 =
      logoFile instanceof File
        ? await fileToBase64(logoFile)
        : await urlToBase64(defaultLogoSrc);
  } catch (e) {
    console.warn("Logo load failed:", e);
  }

  // ── Workbook ──
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(title.slice(0, 31));

  ws.getColumn(1).width = 30;
  for (let c = 2; c <= TOTAL_COLS; c++) ws.getColumn(c).width = 22;

  // ── Row helpers ──
  let r = 0;
  const pushRow = (height) => {
    r++;
    ws.getRow(r).height = height;
    return r;
  };
  const spacer = (h = 8) => pushRow(h);

  // Merge all columns on a new row and style the master cell
  const fullRow = (height, value, font, alignment, fill) => {
    const rowNum = pushRow(height);
    ws.mergeCells(rowNum, 1, rowNum, TOTAL_COLS);
    const cell = ws.getCell(rowNum, 1);
    if (value !== undefined) cell.value = value;
    styleCell(cell, { font, alignment, fill });
    return rowNum;
  };

  // ══════════ HEADER (rows 1-4) ══════════════════════════════════════════════

  fullRow(
    20,
    headerCompanyName,
    { bold: true, size: 11, color: { argb: C.black } },
    { horizontal: "right", vertical: "middle" },
  );

  fullRow(
    16,
    headerFormerName,
    { italic: true, size: 9, color: { argb: C.black } },
    { horizontal: "right", vertical: "middle" },
  );

  fullRow(
    16,
    headerRegnNo,
    { size: 9, color: { argb: C.black } },
    { horizontal: "right", vertical: "middle" },
  );

  fullRow(
    16,
    headerPanNo,
    { size: 9, color: { argb: C.black } },
    { horizontal: "right", vertical: "middle" },
  );

  // Logo overlaying rows 1-4, top-left
  if (logoBase64) {
    const imageId = wb.addImage({ base64: logoBase64, extension: "png" });
    ws.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 160, height: 68 },
      editAs: "oneCell",
    });
  }

  // ── Red separator ──
  const sepRowNum = pushRow(5);
  for (let c = 1; c <= TOTAL_COLS; c++) {
    ws.getCell(sepRowNum, c).border = {
      bottom: { style: "medium", color: { argb: C.red } },
    };
  }

  // ══════════ DATE ══════════════════════════════════════════════════════════

  spacer(8);
  fullRow(
    20,
    toOrdinalDate(date),
    { bold: true, size: 11, color: { argb: C.red } },
    { vertical: "middle" },
  );
  spacer(6);

  // ══════════ TO SECTION ════════════════════════════════════════════════════

  fullRow(16, "To", { size: 10, color: { argb: C.black } });
  fullRow(16, toName, { bold: true, size: 10, color: { argb: C.black } });
  fullRow(16, toAddress, { bold: true, size: 10, color: { argb: C.black } });
  spacer(10);

  // ══════════ SUBJECT ════════════════════════════════════════════════════════

  fullRow(
    18,
    `Subject: ${subject}.`,
    { size: 10, underline: true, color: { argb: C.black } },
    { horizontal: "center", vertical: "middle" },
  );
  spacer(8);

  // ══════════ GREETING ═══════════════════════════════════════════════════════

  fullRow(16, "Dear Sir/Ma'am,", { size: 10, color: { argb: C.black } });
  fullRow(
    16,
    "We are pleased to submit our quotation for your transportation requirements as detailed below:",
    { size: 10, color: { argb: C.black } },
    { wrapText: true },
  );
  spacer(10);

  // ══════════ PRICE GRID ═════════════════════════════════════════════════════

  if (flatProducts.length > 0 && locations.filter(Boolean).length > 0) {
    // Push brand row and product row, then back-fill styles
    const brandRowNum = pushRow(22);
    const prodRowNum = pushRow(50);

    // "Location" header spanning both rows in col 1
    ws.mergeCells(brandRowNum, 1, prodRowNum, 1);
    styleCell(ws.getCell(brandRowNum, 1), {
      font: { bold: true, size: 10, color: { argb: C.white } },
      fill: solidFill(C.red),
      alignment: { horizontal: "center", vertical: "middle" },
      border: allThinGray,
    });
    ws.getCell(brandRowNum, 1).value = "Location";

    // Brand header cells (cols 2+)
    let colCursor = 2;
    brands.forEach((brand) => {
      if (!brand.products.length) return;
      const startCol = colCursor;
      const endCol = colCursor + brand.products.length - 1;
      if (brand.products.length > 1) {
        ws.mergeCells(brandRowNum, startCol, brandRowNum, endCol);
      }
      const cell = ws.getCell(brandRowNum, startCol);
      cell.value = brand.name || "";
      styleCell(cell, {
        font: { bold: true, size: 10, color: { argb: C.white } },
        fill: solidFill(C.red),
        alignment: { horizontal: "center", vertical: "middle" },
        border: allThinGray,
      });
      colCursor += brand.products.length;
    });

    // Product header cells (cols 2+)
    flatProducts.forEach((fp, pIdx) => {
      const cell = ws.getCell(prodRowNum, pIdx + 2);
      cell.value = fp.productName;
      styleCell(cell, {
        font: { bold: true, size: 10, color: { argb: C.white } },
        fill: solidFill(C.red),
        alignment: { horizontal: "center", vertical: "middle", wrapText: true },
        border: allThinGray,
      });
    });

    // Data rows
    locations.filter(Boolean).forEach((location, lIdx) => {
      const rowNum = pushRow(18);
      const rowFill = solidFill(lIdx % 2 === 0 ? C.white : C.lightGray);

      const locCell = ws.getCell(rowNum, 1);
      locCell.value = location;
      styleCell(locCell, {
        font: { bold: true, size: 10, color: { argb: C.black } },
        fill: rowFill,
        alignment: { horizontal: "left", vertical: "middle" },
        border: allThinGray,
      });

      flatProducts.forEach(({ brandName, productName }, pIdx) => {
        const entry = entries.find(
          (e) =>
            e.location === location &&
            e.brandName === brandName &&
            e.productName === productName,
        );
        const cell = ws.getCell(rowNum, pIdx + 2);
        cell.value = entry?.value ?? "";
        styleCell(cell, {
          font: { size: 10, color: { argb: C.black } },
          fill: rowFill,
          alignment: { horizontal: "center", vertical: "middle" },
          border: allThinGray,
        });
      });
    });
  }

  spacer(10);

  // ══════════ CHARGES NOTE ═══════════════════════════════════════════════════

  if (chargesNote) {
    fullRow(
      16,
      chargesNote,
      { size: 9, color: { argb: C.black } },
      { vertical: "middle" },
      solidFill(C.yellow),
    );
  }

  spacer(10);

  // ══════════ TERMS & CONDITIONS ═════════════════════════════════════════════

  fullRow(
    18,
    "Terms & Conditions",
    { bold: true, size: 11, color: { argb: C.red }, underline: true },
    { vertical: "middle" },
  );

  termsAndConditions.forEach((term, idx) => {
    fullRow(
      16,
      `${idx + 1}. ${term}`,
      { size: 9.5, color: { argb: C.black } },
      { wrapText: true, indent: 1 },
    );
  });

  spacer(10);

  // ══════════ CLOSING NOTE ════════════════════════════════════════════════════

  fullRow(16, closingNote, { size: 10, color: { argb: C.black } });
  spacer(8);

  // ══════════ SIGNATORY ═══════════════════════════════════════════════════════

  [
    thankYouNote,
    regards,
    signatoryName,
    signatoryDesignation,
    signatoryCompany,
    signatoryAddress,
    signatoryPhone,
  ]
    .filter(Boolean)
    .forEach((line) =>
      fullRow(16, line, { bold: true, size: 10, color: { argb: C.red } }),
    );

  if (signatoryEmail) {
    fullRow(16, signatoryEmail, {
      bold: true,
      size: 10,
      color: { argb: C.blue },
    });
  }

  spacer(14);

  // ══════════ FOOTER ══════════════════════════════════════════════════════════

  // Red separator
  const footerSepRow = pushRow(5);
  for (let c = 1; c <= TOTAL_COLS; c++) {
    ws.getCell(footerSepRow, c).border = {
      top: { style: "medium", color: { argb: C.red } },
    };
  }

  // Three-section footer row
  const footerRowNum = pushRow(32);
  const leftEnd = Math.ceil(TOTAL_COLS / 3);
  const centerEnd = Math.ceil((TOTAL_COLS * 2) / 3);

  if (leftEnd >= 1) {
    if (leftEnd > 1) ws.mergeCells(footerRowNum, 1, footerRowNum, leftEnd);
    const cell = ws.getCell(footerRowNum, 1);
    cell.value = (footerLeft || "").replace(/\\n/g, "\n");
    styleCell(cell, {
      font: { size: 7.5, color: { argb: C.black } },
      alignment: { wrapText: true, vertical: "middle" },
    });
  }

  if (centerEnd > leftEnd) {
    ws.mergeCells(footerRowNum, leftEnd + 1, footerRowNum, centerEnd);
    const cell = ws.getCell(footerRowNum, leftEnd + 1);
    cell.value = footerCenter || "";
    styleCell(cell, {
      font: { size: 7.5, color: { argb: C.black } },
      alignment: { horizontal: "center", vertical: "middle" },
    });
  }

  if (TOTAL_COLS > centerEnd) {
    ws.mergeCells(footerRowNum, centerEnd + 1, footerRowNum, TOTAL_COLS);
    const cell = ws.getCell(footerRowNum, centerEnd + 1);
    cell.value = `${footerRightWeb}\n${footerRightEmail}`;
    styleCell(cell, {
      font: { size: 7.5, color: { argb: C.black } },
      alignment: { horizontal: "right", wrapText: true, vertical: "middle" },
    });
  }

  return wb; // caller does: await wb.xlsx.writeBuffer()
};
